import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Lock } from "lucide-react";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy p-4">
      <div className="absolute inset-0 grid-lines opacity-40" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="mb-6 flex items-center justify-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-finblue font-heading text-lg font-bold text-white">M</span>
          <span className="font-heading text-lg font-bold text-white">MENA<span className="text-gold">Group</span>FX</span>
        </Link>
        <div className="rounded-2xl border border-white/10 bg-white p-8 shadow-2xl">
          <div className="mb-6 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-canvas text-finblue"><Lock className="h-5 w-5" /></span>
            <div>
              <h1 className="font-heading text-xl font-bold text-ink">Partner & Admin Login</h1>
              <p className="text-sm text-subtle">Access the management dashboard</p>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
            </div>
            {error && <p className="rounded-md bg-danger/10 p-2 text-sm text-danger" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full bg-finblue hover:bg-finblueDark" data-testid="login-submit">
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </div>
        <p className="mt-4 text-center text-xs text-slate-400">Authorised personnel only.</p>
      </div>
    </div>
  );
}
